import { ConfigProvider, Flex, Space } from "antd";
import { useTable } from "@refinedev/antd";
import {
  Authenticated,
  useGo,
  useInvalidate,
  useList,
  useTranslate,
  type CrudFilter,
} from "@refinedev/core";
import {
  AppstoreOutlined,
  DatabaseOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { useQuota } from "hooks/useQuota";
import { QuotaFeatures } from "@/constants";
import { ReactNode, useMemo, useCallback, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { MediaProjectListItem, ProjectStatus } from "../types";
import { useBoundStore, useVolatileBoundStore } from "../../../store";
import {
  ProjectTableViewMode,
  ProjectStatusFilterKey,
  ProjectTypeFilterKey,
} from "./types";
import { selectUserCan } from "helpers/utils";
import { useSession } from "hooks/useSession";
import { useWorkspace } from "hooks/useWorkspace";
import { CreatePrimaryButton } from "components/CreatePrimaryButton";
import { useAntTheme } from "hooks/useAntTheme";
import { WSEvent, NOTIFICATIONS_EVENT_TYPE } from "store/websocketStore/";
import { useWsSubscription } from "hooks/useWsSubscription/";
import { createHandleTableSearch } from "helpers/table/tableUtils";
import { createProjectColumns } from "helpers/table/projectColumns";
import {
  PROJECT_FIELD_COLUMNS,
  MAX_VISIBLE_PROJECT_FIELD_COLUMNS,
  DEFAULT_VISIBLE_PROJECT_FIELD_COLUMNS,
  normalizeVisibleProjectFieldColumns,
  type ProjectFieldColumnKey,
} from "helpers/table/projectFieldColumns";
import {
  ColumnPrefsDndContext,
  PrefsHeaderCell,
  reorderColumns,
} from "helpers/table/columnPrefs";
import { TableFilters } from "helpers/table/TableFilters";
import type { QuickFilterPill } from "helpers/table/QuickFilters";
import { radius } from "@libertify/ui";
import { PageLayout, TABLE_THEME } from "components/PageLayout";
import { pagePaddingCss } from "components/layout/pagePadding";
import { useAnalytics } from "hooks/useAnalytics";
import { useCreateExperienceEntry } from "hooks/useCreateExperienceEntry";
import { AnalyticsEvents } from "types/analytics";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import { useProjectWizardStore } from "./useProjectWizardStore";
import { ProjectsTable } from "../workspaces/tables/projects";
import { ProjectInit } from "../home/components/ProjectInit";
import { UpgradeCard } from "components/UpgradeCard";
import { upgradeCardCookie } from "@/utils/upgradeCardCookie";
import styled from "styled-components";
import { ArrowRightIcon } from "@phosphor-icons/react";
import {
  useOrganizationSummaries,
  ProjectSessionSummary,
} from "../home/hooks/useOrganizationAnalytics";
import { colors } from "@libertify/ui/theme-v2";

export const INNER_CARD_BORDER_RADIUS = radius.box;
export const PAGE_SIZE = 20;

// Status pills are mutually exclusive — each maps to a single server-side
// ProjectStatus (or none for "all"). Mirrors the analytics page's pills.
const STATUS_LABEL_KEYS: Record<ProjectStatusFilterKey, string> = {
  all: "media.components.projectQuickAccess.all",
  published: "media.components.MediaStatus.Published",
  ready: "media.components.projectQuickAccess.ready",
};
const STATUS_FILTER_KEYS = Object.keys(
  STATUS_LABEL_KEYS
) as ProjectStatusFilterKey[];
const STATUS_BY_FILTER: Record<
  ProjectStatusFilterKey,
  ProjectStatus | undefined
> = {
  all: undefined,
  published: "Published",
  ready: "MediaGenerated",
};

// Type pills have no "All" option — clicking the active one again clears it.
const TYPE_LABEL_KEYS: Record<ProjectTypeFilterKey, string> = {
  custom: "media.projects.index.filterCustom",
  autopilot: "media.projects.index.filterAutopilot",
};
const TYPE_FILTER_KEYS = Object.keys(TYPE_LABEL_KEYS) as ProjectTypeFilterKey[];
const TYPE_VALUE_BY_FILTER: Record<ProjectTypeFilterKey, string> = {
  custom: "new_video",
  autopilot: "flash",
};

// Emits the full filter set (search + status + type) so callers can use
// "replace" without one input clobbering the others.
const buildProjectFilters = (
  search: string,
  status: ProjectStatusFilterKey,
  type: ProjectTypeFilterKey | null
): CrudFilter[] => {
  const filters: CrudFilter[] = [];
  if (search) {
    filters.push({ field: "search_text", operator: "eq", value: search });
  }
  const projectStatus = STATUS_BY_FILTER[status];
  if (projectStatus) {
    filters.push({ field: "status", operator: "eq", value: projectStatus });
  }
  if (type) {
    filters.push({
      field: "type",
      operator: "eq",
      value: TYPE_VALUE_BY_FILTER[type],
    });
  }
  return filters;
};

export const ProjectsList = () => {
  const t = useTranslate();
  const go = useGo();
  const invalidate = useInvalidate();
  const { currentOrganization, accesses } = useSession();
  const { workspaceId, isLoading: isLoadingWorkspaces } = useWorkspace();
  const projectsState = useBoundStore((state) => state.projectsState);
  const setProjectsState = useBoundStore((state) => state.setProjectsState);
  const {
    searchText,
    columnOrder,
    // Older persisted state (pre-migration, still hydrated as version 4 from
    // a stale localStorage entry) can lack these keys — default rather than
    // let a missing key surface as "everything hidden"/"no filter active".
    visibleFieldColumns = DEFAULT_VISIBLE_PROJECT_FIELD_COLUMNS,
    statusFilter = "all",
    typeFilter = null,
  } = projectsState;
  const { theme } = useAntTheme();
  const { isBlocked } = useQuota();
  const { isTrialExpired, paywallFlow } = useSoftPaywall();
  const isProjectQuotaReached =
    isBlocked(QuotaFeatures.MediaProject) || isTrialExpired;
  const { trackEvent } = useAnalytics();
  const { setWizardStartTime, setHasNoProjects } = useProjectWizardStore();
  const { startCreateExperience, chooserModal } = useCreateExperienceEntry();
  const [showUpgradeCard, setShowUpgradeCard] = useState(() => {
    return isProjectQuotaReached && !upgradeCardCookie.isDismissed();
  });

  const { setCurrentMenuIndex } = useVolatileBoundStore((store) => ({
    setCurrentMenuIndex: store.setCurrentMenuIndex,
  }));

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (workspaceId) {
      params.append("workspace_id", workspaceId);
    }
    const queryStr = params.toString();
    return queryStr ? `?${queryStr}` : "";
  }, [workspaceId]);

  const resource = `media/${currentOrganization?.id}/projects${queryString}`;

  // Recent projects for QuickAccess (limited to 6, sorted by creation date)
  const { data: recentProjects } = useList<MediaProjectListItem>({
    resource,
    pagination: {
      pageSize: 6,
    },
    sorters: [{ field: "created", order: "desc" }],
  });

  // It manages table data with filters and pagination
  const {
    tableQuery: { data: projectsData },
    tableProps,
    setCurrent,
    setFilters,
  } = useTable<MediaProjectListItem>({
    resource,
    queryOptions: {
      enabled: Boolean(currentOrganization) && Boolean(!isLoadingWorkspaces),
      refetchOnMount: "always",
    },
    pagination: {
      pageSize: PAGE_SIZE,
    },
    sorters: {
      initial: [
        {
          field: "created",
          order: "desc",
        },
      ],
    },
    filters: {
      initial: buildProjectFilters(searchText, statusFilter, typeFilter),
    },
  });

  const currentPageProjectIds = useMemo(
    () => projectsData?.data.map((p) => p.id) ?? [],
    [projectsData]
  );

  // Scope the batch call to the projects on the current table page; wait for them to load.
  const { summaries } = useOrganizationSummaries(currentOrganization?.id, {
    page: 1,
    size: PAGE_SIZE,
    projectIds: currentPageProjectIds,
    enabled: currentPageProjectIds.length > 0,
  });

  const summariesMap = useMemo(() => {
    const map = new Map<string, ProjectSessionSummary>();
    summaries?.forEach((s) => map.set(s.project_id, s));
    return map;
  }, [summaries]);

  const debouncedSetFilters = useDebouncedCallback((search: string) => {
    setFilters(
      buildProjectFilters(search, statusFilter, typeFilter),
      "replace"
    );
  }, 300);

  const handleTableSearch = createHandleTableSearch((state) => {
    setCurrent(1);
    setProjectsState(state);
    debouncedSetFilters(state.searchText ?? "");
  });

  // Applies status + type together so "All" can reset both atomically —
  // calling two separate setters back-to-back would have the second one
  // read the other's pre-update value from a stale closure.
  const applyProjectFilters = useCallback(
    (status: ProjectStatusFilterKey, type: ProjectTypeFilterKey | null) => {
      setCurrent(1);
      setProjectsState({ statusFilter: status, typeFilter: type });
      setFilters(buildProjectFilters(searchText, status, type), "replace");
    },
    [setCurrent, setProjectsState, setFilters, searchText]
  );

  const toggleFieldColumn = useCallback(
    (key: ProjectFieldColumnKey) => {
      const isOn = visibleFieldColumns.includes(key);
      if (
        !isOn &&
        visibleFieldColumns.length >= MAX_VISIBLE_PROJECT_FIELD_COLUMNS
      ) {
        return;
      }
      setProjectsState({
        visibleFieldColumns: isOn
          ? visibleFieldColumns.filter((k) => k !== key)
          : normalizeVisibleProjectFieldColumns([...visibleFieldColumns, key]),
      });
    },
    [visibleFieldColumns, setProjectsState]
  );

  const quickFilterPills: QuickFilterPill[] = useMemo(
    () => [
      ...STATUS_FILTER_KEYS.map((key) => ({
        key,
        label: t(STATUS_LABEL_KEYS[key]),
        // "All" means no filters at all, so it's only active when there's
        // also no type filter — and clicking it clears the type filter too.
        active:
          key === "all"
            ? statusFilter === "all" && typeFilter === null
            : statusFilter === key,
        onClick: () => {
          if (key === "all") {
            applyProjectFilters("all", null);
          } else {
            const statusKey = statusFilter === key ? "all" : key;
            applyProjectFilters(statusKey, typeFilter);
          }
        },
      })),
      ...TYPE_FILTER_KEYS.map((key) => ({
        key,
        label: t(TYPE_LABEL_KEYS[key]),
        active: typeFilter === key,
        onClick: () =>
          applyProjectFilters(statusFilter, typeFilter === key ? null : key),
      })),
    ],
    [t, statusFilter, typeFilter, applyProjectFilters]
  );

  const fieldDropdownColumns = useMemo(
    () =>
      PROJECT_FIELD_COLUMNS.map((c) => ({
        key: c.key,
        label: t(`media.projects.index.${c.labelKey}`),
        removable: c.removable,
      })),
    [t]
  );

  const isFieldColumnVisible = useCallback(
    (key: ProjectFieldColumnKey) => visibleFieldColumns.includes(key),
    [visibleFieldColumns]
  );

  //Handler for WebSocket events - stable reference
  const handleStatusUpdate = useCallback(
    (event: WSEvent) => {
      if (!event.project_id) {
        return;
      }
      if (event.event !== NOTIFICATIONS_EVENT_TYPE.ProjectStatusChanged) {
        return;
      }
      // Invalidate the query to refetch updated data from server
      invalidate({
        resource,
        invalidates: ["list"],
      }).catch((error) => {
        console.error("Failed to invalidate projects list:", error);
      });
    },
    [invalidate, resource]
  );

  const subscriptionConfigs = useMemo(() => {
    return (
      projectsData?.data.map((p) => ({
        type: "project" as const,
        id: p.id,
      })) ?? []
    );
  }, [projectsData]);

  useWsSubscription(subscriptionConfigs, handleStatusUpdate);

  const viewOptions = [
    {
      value: "list",
      icon: <MenuOutlined />,
    },
    {
      value: "grid",
      icon: <AppstoreOutlined />,
    },
  ] as { value: ProjectTableViewMode; icon: ReactNode }[];

  // for admins to see easily all projects
  const canSeeAllProjects = selectUserCan(accesses, {
    subject: "Media.Project",
    action: "read",
    scope: "ALL",
  });
  if (canSeeAllProjects) {
    viewOptions.push({
      value: "listAllOrgs",
      icon: <DatabaseOutlined />,
    });
  }

  const handleProjectClick = useCallback(
    (record: MediaProjectListItem) => {
      //reset project page store
      setCurrentMenuIndex(null);
      go({
        to: {
          resource: "media_projects",
          id: record.id,
          action: "show",
        },
      });
    },
    [setCurrentMenuIndex, go]
  );

  const handleColumnOrderChange = useCallback(
    (order: string[]) => {
      setProjectsState({ columnOrder: order });
    },
    [setProjectsState]
  );

  const columns = useMemo(
    () =>
      reorderColumns(
        createProjectColumns({
          t,
          handleProjectClick,
          organizationFilterValue: projectsState.filters.organization_id,
          theme: {
            colorLightGrey: theme.colorLightGrey,
            colorTextDescription: theme.colorTextDescription,
          },
          invalidationResource: resource,
          summariesMap,
          visibleFieldColumns,
        }),
        columnOrder,
        "actions"
      ),
    [
      t,
      handleProjectClick,
      projectsState.filters.organization_id,
      theme.colorLightGrey,
      theme.colorTextDescription,
      resource,
      summariesMap,
      columnOrder,
      visibleFieldColumns,
    ]
  );

  const tableComponents = useMemo(
    () => ({ header: { cell: PrefsHeaderCell } }),
    []
  );

  const hasNoProjects = recentProjects?.data.length === 0;

  useEffect(() => {
    if (recentProjects !== undefined) {
      setHasNoProjects(hasNoProjects);
    }
  }, [hasNoProjects, recentProjects, setHasNoProjects]);

  return (
    <Authenticated key="projectsList">
      {hasNoProjects ? (
        <StyledEmptyPage vertical>
          <ProjectInit />
        </StyledEmptyPage>
      ) : (
        <StyledExperiencesLayout
          $quotaReached={showUpgradeCard}
          title={t("media.projects.index.title")}
          description={t("media.projects.index.description")}
          breadcrumbLabel={t("src.App.projects")}
          actions={
            <StyledHeaderActions>
              <Flex align="center" gap={16}>
                {isProjectQuotaReached && (
                  <StyledUpgradeLink
                    onClick={() => go({ to: "/subscription/subscriptions" })}
                  >
                    {t("quota.upgradeToKeepCreating")}{" "}
                    <ArrowRightIcon size={16} />
                  </StyledUpgradeLink>
                )}
                <CreatePrimaryButton
                  disabled={isProjectQuotaReached}
                  style={
                    isProjectQuotaReached
                      ? {
                          backgroundColor: theme.colorBgDisabled,
                          color: theme.colorTextBtnDisabled,
                          borderColor: theme.colorBorderDisabled,
                          opacity: 1,
                        }
                      : undefined
                  }
                  onClick={() => {
                    setWizardStartTime(Date.now());
                    trackEvent(AnalyticsEvents.ProjectCreateInit, {
                      page: "project_list",
                      paywall_flow: paywallFlow,
                    });
                    startCreateExperience();
                  }}
                >
                  {t("media.projects.index.createAProject")}
                </CreatePrimaryButton>
                {chooserModal}
              </Flex>
              {showUpgradeCard && (
                <StyledUpgradeCardPositioner>
                  <UpgradeCard onDismiss={() => setShowUpgradeCard(false)} />
                </StyledUpgradeCardPositioner>
              )}
            </StyledHeaderActions>
          }
        >
          {/* Filters and table text in Inter (only the font token changes). */}
          <ConfigProvider theme={TABLE_THEME}>
            <Space
              direction="vertical"
              size={0}
              style={{
                width: "100%",
                backgroundColor: colors.surface.base,
                borderRadius: radius.box,
                border: `1px solid ${colors.border.subtle}`,
                overflow: "hidden",
                minHeight: 400,
              }}
            >
              <TableFilters
                quickFilters={{
                  label: t("media.projects.index.quickFilters.label"),
                  pills: quickFilterPills,
                }}
                fieldsDropdown={{
                  columns: fieldDropdownColumns,
                  isVisible: isFieldColumnVisible,
                  toggleColumn: toggleFieldColumn,
                  canAddMore:
                    visibleFieldColumns.length <
                    MAX_VISIBLE_PROJECT_FIELD_COLUMNS,
                  title: t("media.projects.index.tableFields.title"),
                  buttonLabel: t("media.projects.index.tableFields.button"),
                  hint: t("media.projects.index.tableFields.hint", {
                    count: MAX_VISIBLE_PROJECT_FIELD_COLUMNS,
                  }),
                }}
                searchValue={searchText}
                onSearchChange={handleTableSearch}
                searchPlaceholder={t("media.projects.index.q")}
              />
              <ColumnPrefsDndContext
                order={columnOrder}
                onOrderChange={handleColumnOrderChange}
              >
                <ProjectsTable
                  tableProps={tableProps}
                  columns={columns}
                  components={tableComponents}
                />
              </ColumnPrefsDndContext>
            </Space>
          </ConfigProvider>
        </StyledExperiencesLayout>
      )}
    </Authenticated>
  );
};

// Empty state (ProjectInit): its own background (BackgroundImage) bleeds past
// the page padding, which is applied again here.
const StyledEmptyPage = styled(Flex)`
  min-height: 100vh;
  ${({ theme }) => pagePaddingCss(theme)}
`;

// Shared page layout; when the project quota is reached, a teal glow sits over
// the top-right corner (behind the header actions, which have a higher z-index).
const StyledExperiencesLayout = styled(PageLayout)<{ $quotaReached: boolean }>`
  &::before {
    content: ${({ $quotaReached }) => ($quotaReached ? '""' : "none")};
    position: absolute;
    inset: 0;
    background: radial-gradient(
      52.7% 33.7% at 91.93% 6.98%,
      rgba(26, 203, 174, 0.7) 0%,
      rgba(26, 203, 174, 0) 100%
    );
    pointer-events: none;
    z-index: 50;
  }
`;

const StyledHeaderActions = styled.div`
  position: relative;
  z-index: 60;
`;

const StyledUpgradeCardPositioner = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  z-index: 100;
`;

const StyledUpgradeLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6946d5;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #711bb2;
  }
`;
